import { useState } from 'react';
import omit from 'lodash/omit';
import { useNavigate } from 'react-router-dom';

import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CloseIcon from '@mui/icons-material/Close';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import SwapVertIcon from '@mui/icons-material/SwapVert';

import config from '../../config/config';
import CustomCheckbox from '../common/CustomCheckbox';
import CustomSelect from '../common/CustomSelect';
import FindPatientForm from '../patients/FindPatientForm';
import SelectProviderInput from '../providers/SelectProviderInput';
import {getClaimStatuses} from '../../helpers/selectOptions';

const sorts = {
  '-date_created': 'Date Created (Newest first)',
  'date_created': 'Date Created (Oldest first)',
  '-session__date': 'Session Date (Newest first)',
  'session__date': 'Session Date (Oldest first)',
};

export const SortButton = props => {
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClose = () => setAnchorEl(null);
  return (
      <Box className="sort-button-box">
      <Stack direction='row' spacing={1} alignItems='center'>
        <Button
          id='sort-menu-btn'
          variant='grayOutline'
          aria-controls={!!anchorEl ? 'sort-menu-btn' : undefined}
          aria-haspopup='true'
          aria-expanded={!!anchorEl ? 'true' : undefined}
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{boxShadow: config().styles.defaultBoxShadow}}
        >
          <Typography variant='large500Tight' color={config().colors.gray}>
            {sorts[props.sort] || sorts['-date_created']}
          </Typography>
          <SwapVertIcon sx={{fontSize: '1.5rem', color: 'var(--primary)'}}/>
        </Button>
        <Menu
          id='sort-menu'
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          disableScrollLock={true}
        >
          {
            Object.keys(sorts).map(k =>
              <MenuItem
                onClick={e => {
                  props.onChange(e, k);
                  handleClose();
                }}
              >
                {sorts[k]}
              </MenuItem>
            )
          }
        </Menu>
      </Stack>
    </Box>
  );
};

const CaseFilters = props => {
  const {filters} = props;
  const navigate = useNavigate();
  return (
    <Stack direction='row' spacing={{xs: 1.25, md: 3.65}} paddingTop={1.5} paddingBottom={1.5} fullWidth>
        <Stack direction='row' justifyContent='space-between' alignItems='center' spacing={1}>
          <Box style={{minWidth: '200px'}}>
            <FindPatientForm
              rounded={true}
              defaultValue={filters?.patient_id}
              allPatients={true}
              fullWidth={true}
              color='primary'
              onSubmit={({patientId}) => {
                let _filters = (
                  patientId
                  ? {...filters, patient_id: patientId}
                  : omit(filters, 'patient_id')
                );
                props.onFilterChange(_filters);
              }}
            />
          </Box>
          {
            filters.provider_id
            ? <Chip
              label='Filtering by Provider'
              onDelete={() => props.onFilterChange(omit(filters, 'provider_id'))}
              deleteIcon={<CloseIcon />}
              style={{minWidth: '200px'}}
            />
            : <SelectProviderInput
              label={null}
              placeholder='Filter by Provider'
              defaultValue={filters?.provider_id}
              style={{minWidth: '200px'}}
              onChange={e => {
                if (e.target?.type === 'text') return () => true;
                return (
                  e.target.value
                  ? props.onFilterChange({...filters, provider_id: e.target.value})
                  : props.onFilterChange(omit(filters, 'provider_id'))
                );
              }}
            />
          }
          {
            !filters.status 
            ? <CustomSelect
              placeholder='Filter by Status'
              options={getClaimStatuses()}
              style={{minWidth: '200px'}}
              onChange={e => {
                if (e.target?.type === 'text') return () => true;
                props.onFilterChange({...filters, status: e.target.value});
              }}
            />
            : <Chip 
              label={getClaimStatuses().find(s => s.value === filters.status)?.label || 'All'}
              onDelete={() => props.onFilterChange(omit(filters, 'status'))}
              deleteIcon={<CloseIcon />}
              style={{minWidth: '200px'}}
            />
          }
          <CustomCheckbox
            label='Show Unpaid Only'
            checked={filters.unpaid || false}
            onChange={e => props.onFilterChange(
              e.target.checked
              ? {...filters, unpaid: e.target.checked}
              : omit(filters, 'unpaid')
            )}
          />
          <SortButton
            sort={filters?.sort}
            onChange={(e, sort) => props.onFilterChange({...filters, sort})}
          />
        </Stack>
    </Stack>
  );
};

export default CaseFilters;